import { useEffect, useState, MouseEvent } from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  Popover,
  IconButton,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import TextInput from 'src/components/TextInput';
import { ReactComponent as CampaignsIcon } from 'src/assets/icons/campaigns.svg';
import useCampaign from 'src/hooks/useCampaign';
import useNotification from 'src/hooks/useNotification';
import { getAlignmentSummary } from 'src/apis/campaign';
import { Preference } from 'src/types/preference';
import { CampaignProps } from 'src/types/campaign';
import { PreferencesModal } from 'src/modals/PreferencesModal';
import { generateCampaignReportPptx } from 'src/utils/campaignReportPptx';
import { CampaignSelectorModal } from './CampaignSelectorModal';

const MONTH_OPTIONS = [
  { label: 'January', short: 'Jan', value: 1 },
  { label: 'February', short: 'Feb', value: 2 },
  { label: 'March', short: 'Mar', value: 3 },
  { label: 'April', short: 'Apr', value: 4 },
  { label: 'May', short: 'May', value: 5 },
  { label: 'June', short: 'Jun', value: 6 },
  { label: 'July', short: 'Jul', value: 7 },
  { label: 'August', short: 'Aug', value: 8 },
  { label: 'September', short: 'Sep', value: 9 },
  { label: 'October', short: 'Oct', value: 10 },
  { label: 'November', short: 'Nov', value: 11 },
  { label: 'December', short: 'Dec', value: 12 },
] as const;

type ReportPeriod = {
  year: number;
  month: number;
};

interface NewReportModalProps {
  open: boolean;
  closeModal: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    logo: {
      width: 50,
      height: 50,
      padding: 12,
      borderRadius: 8,
      border: '1px solid var(--Gray-200, #EAECF0)',
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      marginTop: 20,
    },
    inputField: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    input: {
      padding: '6px 8px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',
      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    dropdown: {
      padding: '10px 14px',
      borderRadius: 8,
      width: '100%',
      height: 52,
      marginTop: 0,
      justifyContent: 'flex-start',
      '& p': {
        fontSize: 16,
        fontWeight: 400,
      },
    },
    periodPopover: {
      padding: 12,
      width: 280,
    },
    periodYearRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    periodMonthGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 4,
    },
    periodMonthButton: {
      minWidth: 0,
      height: 36,
      borderRadius: 6,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 500,
      color: '#344054',
      '&:hover': {
        background: '#F2F4F7',
      },
    },
    periodMonthSelected: {
      background: '#101828',
      color: '#FFFFFF',
      '&:hover': {
        background: '#101828',
      },
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      '& button': {
        flex: 1,
      },
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 520,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

const formatReportPeriodLabel = (period: ReportPeriod): string => {
  const monthLabel =
    MONTH_OPTIONS.find((option) => option.value === period.month)?.label || '';
  return `${monthLabel} ${period.year}`;
};

export const NewReportModal = ({ open, closeModal }: NewReportModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { campaigns } = useCampaign();
  const { handleError } = useNotification();

  const [brandName, setBrandName] = useState('');
  const [period, setPeriod] = useState<ReportPeriod | null>(null);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [selectedPolicy, setSelectedPolicy] = useState<Preference | null>(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState<CampaignProps[]>(
    []
  );
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [periodAnchorEl, setPeriodAnchorEl] = useState<HTMLElement | null>(
    null
  );

  useEffect(() => {
    if (open) {
      setBrandName('');
      setPeriod(null);
      setPickerYear(new Date().getFullYear());
      setSelectedPolicy(null);
      setSelectedCampaigns([]);
      setHasError(false);
      setLoading(false);
      setPolicyModalOpen(false);
      setCampaignModalOpen(false);
      setPeriodAnchorEl(null);
    }
  }, [open]);

  const selectedPeriodLabel = period ? formatReportPeriodLabel(period) : null;

  const isValid =
    !!brandName.trim() &&
    period != null &&
    !!selectedPolicy &&
    selectedCampaigns.length > 0;

  const handleClose = () => {
    if (loading) {
      return;
    }
    setPolicyModalOpen(false);
    setCampaignModalOpen(false);
    setPeriodAnchorEl(null);
    closeModal();
  };

  const handleOpenPeriodPicker = (event: MouseEvent<HTMLElement>) => {
    setPickerYear(period?.year ?? new Date().getFullYear());
    setPeriodAnchorEl(event.currentTarget);
  };

  const handleSelectMonth = (month: number) => {
    setPeriod({ year: pickerYear, month });
    setPeriodAnchorEl(null);
  };

  const handleGenerate = async () => {
    if (!isValid || !selectedPeriodLabel || !selectedPolicy) {
      setHasError(true);
      return;
    }

    setLoading(true);
    try {
      const summary = await getAlignmentSummary({
        campaign_ids: selectedCampaigns.map((campaign) => campaign.id),
        preference_id: selectedPolicy.id,
      });

      await generateCampaignReportPptx({
        brandName: brandName.trim(),
        monthLabel: selectedPeriodLabel,
        summary,
      });
      setPolicyModalOpen(false);
      setCampaignModalOpen(false);
      setPeriodAnchorEl(null);
      closeModal();
    } catch (error) {
      handleError(error, 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='new-report-modal-title'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <Box className={classes.header}>
            <Box className={classes.logo}>
              <CampaignsIcon
                fill='none'
                stroke='#344054'
                strokeWidth={1}
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ width: 24, height: 24 }}
              />
            </Box>
            <CancelIcon
              cursor='pointer'
              width={24}
              height={24}
              onClick={handleClose}
            />
          </Box>
          <Typography
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            New Report
          </Typography>
          <Box className={classes.content}>
            <Box className={classes.inputField}>
              <Typography variant='body1' style={{ fontSize: 14 }}>
                Brand Name
              </Typography>
              <TextInput
                style={
                  hasError && !brandName.trim()
                    ? { border: '1px solid #B42318' }
                    : undefined
                }
                default='Enter brand name'
                value={brandName}
                onChange={setBrandName}
                className={classes.input}
              />
            </Box>
            <Box className={classes.inputField}>
              <Typography variant='body1' style={{ fontSize: 14 }}>
                Month / Year
              </Typography>
              <Button
                variant='outlined'
                height={46}
                onClick={handleOpenPeriodPicker}
                className={classes.dropdown}
                style={{
                  justifyContent: 'flex-start',
                  padding: '6px 8px',
                  border:
                    hasError && period == null
                      ? '1px solid #B42318'
                      : undefined,
                }}
              >
                <Typography
                  style={{
                    fontSize: 16,
                    fontWeight: 400,
                    color: selectedPeriodLabel ? '#344054' : '#667085',
                    textAlign: 'left',
                  }}
                >
                  {selectedPeriodLabel || 'Select month and year'}
                </Typography>
              </Button>
              <Popover
                id='report-period-picker'
                open={Boolean(periodAnchorEl)}
                anchorEl={periodAnchorEl}
                onClose={() => setPeriodAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box className={classes.periodPopover}>
                  <Box className={classes.periodYearRow}>
                    <IconButton
                      size='small'
                      aria-label='Previous year'
                      onClick={() => setPickerYear((year) => year - 1)}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: '#101828',
                      }}
                    >
                      {pickerYear}
                    </Typography>
                    <IconButton
                      size='small'
                      aria-label='Next year'
                      onClick={() => setPickerYear((year) => year + 1)}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                  <Box className={classes.periodMonthGrid}>
                    {MONTH_OPTIONS.map((option) => {
                      const selected =
                        period?.year === pickerYear &&
                        period?.month === option.value;
                      return (
                        <button
                          key={option.value}
                          type='button'
                          className={`${classes.periodMonthButton}${
                            selected ? ` ${classes.periodMonthSelected}` : ''
                          }`}
                          onClick={() => handleSelectMonth(option.value)}
                        >
                          {option.short}
                        </button>
                      );
                    })}
                  </Box>
                </Box>
              </Popover>
            </Box>
            <Box className={classes.inputField}>
              <Typography variant='body1' style={{ fontSize: 14 }}>
                Policy
              </Typography>
              <Button
                variant='outlined'
                height={46}
                onClick={() => setPolicyModalOpen(true)}
                className={classes.dropdown}
                style={{
                  justifyContent: 'flex-start',
                  padding: '6px 8px',
                  border:
                    hasError && !selectedPolicy
                      ? '1px solid #B42318'
                      : undefined,
                }}
              >
                <Typography
                  style={{
                    fontSize: 16,
                    fontWeight: 400,
                    color: selectedPolicy ? '#344054' : '#667085',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                  }}
                >
                  {selectedPolicy?.name || 'Select policy'}
                </Typography>
              </Button>
            </Box>
            <Box className={classes.inputField}>
              <Typography variant='body1' style={{ fontSize: 14 }}>
                Campaigns
              </Typography>
              <Button
                variant='outlined'
                height={46}
                onClick={() => setCampaignModalOpen(true)}
                className={classes.dropdown}
                style={{
                  justifyContent: 'flex-start',
                  padding: '6px 8px',
                  border:
                    hasError && selectedCampaigns.length === 0
                      ? '1px solid #B42318'
                      : undefined,
                }}
              >
                <Typography
                  style={{
                    fontSize: 16,
                    fontWeight: 400,
                    color: selectedCampaigns.length ? '#344054' : '#667085',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                  }}
                >
                  {selectedCampaigns.length === 1
                    ? selectedCampaigns[0].name
                    : selectedCampaigns.length > 1
                    ? `${selectedCampaigns.length} campaigns selected`
                    : 'Select campaigns'}
                </Typography>
              </Button>
            </Box>
          </Box>
          <Box className={classes.buttons}>
            <Button
              title='Cancel'
              variant='outlined'
              height={46}
              onClick={handleClose}
            />
            <Button height={46} onClick={handleGenerate} disabled={loading}>
              {loading ? (
                <CircularProgress style={{ color: '#fff' }} size={24} />
              ) : (
                <span>Generate</span>
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
      <PreferencesModal
        open={policyModalOpen}
        closeModal={() => setPolicyModalOpen(false)}
        singleSelect
        selectedPreferenceIds={selectedPolicy ? [selectedPolicy.id] : []}
        onSelect={(preferences) => {
          setSelectedPolicy(preferences[0] || null);
          setPolicyModalOpen(false);
        }}
      />
      <CampaignSelectorModal
        open={campaignModalOpen}
        closeModal={() => setCampaignModalOpen(false)}
        campaigns={campaigns.filter(
          (campaign) => campaign.status === 'completed'
        )}
        selectedCampaignIds={selectedCampaigns.map((campaign) => campaign.id)}
        onSelect={(selected) => {
          setSelectedCampaigns(selected);
          setCampaignModalOpen(false);
        }}
      />
    </>
  );
};
